import React, { useState, useRef, useEffect } from 'react';
import InfoTooltip from '@common_components/others/infoTooltip.component';
import ValidationIndicator from '@common_components/others/validationIndicator.component';
import { MinusIcon } from '@common_components/icons/minus.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';

interface InputNumberProps {
  className?: string;
  label?: string;
  tooltip?: string;
  value?: number;
  min?: number;
  max?: number;
  textSize?: string;
  onChange?: ( value: number ) => void;
  disable?: boolean;
  isRequired?: boolean;
  labelPosition?: 'top' | 'left';
  icon?: React.ReactNode;
  step?: number;
  labelClassName?: string;
}

const InputNumber: React.FC<InputNumberProps> = ( {
  className = 'w-full',
  label,
  isRequired = false,
  tooltip,
  textSize = 'w-3/12 mr-4',
  disable = false,
  min = 0,
  max = 10,
  value = 0,
  icon,
  step = 1,
  onChange,
  labelPosition = 'left',
  labelClassName = '',
} ) => {
  const [ inputValue, setInputValue ] = useState<number>( value );
  const inputRef = useRef<HTMLInputElement>( null );

  useEffect( () => {
    setInputValue( value );
  }, [ value ] );

  const handleChange = ( newValue: number ) => {
    if ( newValue >= min && newValue <= max ) {
      setInputValue( newValue );
      if ( onChange != undefined ) {
        onChange( newValue );
      }
    }
  };

  const increment = () => {
    if ( disable ) {return;}
    const next = max === undefined ? inputValue + 1 : Math.min( inputValue + step, max );
    handleChange( next );
  };

  const decrement = () => {
    if ( disable ) {return;}
    const next = min === undefined ? inputValue + 1 : Math.max( inputValue - step, min );
    handleChange( next );
  };

  const handleKeyDown = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete',
    ];

    if ( !/^\d$/.test( e.key ) && !allowedKeys.includes( e.key ) ) {
      e.preventDefault();
    }

    if ( e.key === 'ArrowUp' ) {
      e.preventDefault();
      increment();
    } else if ( e.key === 'ArrowDown' ) {
      e.preventDefault();
      decrement();
    }
  };

  return (
    <div className={`rounded-md ${className} ${labelPosition === 'left' ? 'flex' : ''}`} >
      {label && (
        <div className={`flex gap-2 relative items-center ${textSize}`}>
          <label className={`text-gray-600 truncate ${labelPosition === 'left' ? 'flex items-center' : ''} ${labelClassName}`}>
            {label}
          </label>
          {tooltip && (
            <InfoTooltip label={label} tooltip={tooltip} />
          )}
        </div>
      )}

      <div className={`relative w-full ${labelPosition === 'top' && 'mt-2'} flex items-center`}>
        <ValidationIndicator isRequired={isRequired} isValid={ isRequired } />
        {icon && <div className="absolute left-4 text-gray-500">{icon}</div>}
        <input
          ref={inputRef}
          disabled={disable}
          value={ inputValue }
          type={'number'}
          min={min}
          max={max}
          onChange={ event => { handleChange( parseInt( event.target.value ) ); } }
          onKeyDown={handleKeyDown}
          className={`${icon ? 'pl-16' : 'pl-4'} p-2 w-full shadow min-h-11 outline-none rounded-md border-[1px] ${
            disable ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'
          } hover:border-black focus:border-black focus:ring-1 focus:ring-black`}
          onPaste={e => {
            const pasted = e.clipboardData.getData( 'text' );
            if ( !/^\d+$/.test( pasted ) ) {
              e.preventDefault();
            }
          }}
        />

        <div className="absolute right-0 top-0 pr-2 h-full flex items-center gap-2">
          <MinusIcon
            size={7}
            className={`cursor-pointer hover:bg-gray-200 trans-fast rounded-md p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`}
            onClick={decrement}
          />
          <PlusIcon
            size={7}
            className={`cursor-pointer hover:bg-gray-200 trans-fast rounded-md p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`}
            onClick={increment}
          />
        </div>
      </div>
    </div>
  );
};

export default InputNumber;
